import cookies from 'js-cookie';
import { paperlessBillingApi, portalSharedApi, getSelectedCustomerAccountId, removeErrors } from './api';
import { select } from '../shared/utils/dom';
import { delimiters } from '../shared/constants';
import loadingMixin from '../shared/vue/mixins/loading';
import scrollUp from '../shared/vue/mixins/scrollUp';
import { regex } from '../shared/constants/validation';
import dataLayerPush from '../shared/utils/analytics';

const paperlessEmailValidationCountCookieName = 'paperlessEmailValidationCount';
/*
* Enroll in paperless billing
*/
const el = select('[data-vue-app="paperless-billing"]');
if (el) {
  const app = new Vue({ // eslint-disable-line no-unused-vars
    el,
    mixins: [loadingMixin, scrollUp],
    delimiters,
    data: {
      // for accessibility in live regions
      liveText: '',
      success: false,
      isEnrolled: false,
      paperlessAgree: false,
      paperlessEmail: '',
      webAccountEmail: '',
      ebillEmail: '',
      accountId: '',
      isCheckfree: false,
      showCheckFreeWarning: false,
      showEbill: true,
      hasMultiplePaperlessEmails: false,
      update: false,
      emailValidationFailed: false,
      emailValidationFailedCount: 0,
      emailValidationUpdateFailedCount: 0,
    },

    /**
     * Mounted
     */
    mounted() {
      if (window.fabricator) {
        this.isCheckfree = true;
        this.showCheckFreeWarning = true;
      } else {
        this.accountId = getSelectedCustomerAccountId();
      }

      if (this.$el.dataset.email) {
        this.paperlessEmail = this.$el.dataset.email;
        this.webAccountEmail = this.$el.dataset.email.toLowerCase();
      }

      if (this.$el.dataset.ebillEmail) {
        this.ebillEmail = this.$el.dataset.ebillEmail.toLowerCase();
      }

      if (this.$el.dataset.isEnrolled) {
        this.isEnrolled = JSON.parse(this.$el.dataset.isEnrolled);
      }
      if (this.$el.dataset.isCheckfree) {
        this.isCheckfree = JSON.parse(this.$el.dataset.isCheckfree);
        this.showCheckFreeWarning = this.isCheckfree;
      }
      if (this.$el.dataset.showEbill) {
        this.showEbill = JSON.parse(this.$el.dataset.showEbill);
      }

      if (this.isEnrolled && this.ebillEmail !== this.webAccountEmail) {
        this.hasMultiplePaperlessEmails = true;
      }
      // get the submit URL
      const formTargetEl = select('#paperlessEnrollTarget', this.$el);
      if (formTargetEl) {
        this.formTarget = formTargetEl.value;
      }
      this.emailValidationFailedCount = cookies.get(paperlessEmailValidationCountCookieName) ? JSON.parse(cookies.get(paperlessEmailValidationCountCookieName)) : 0;
    },

    /**
     * Created
     */
    created() {
      Event.$on('A11yDialogConfirm:confirm-ebill-cancel', () => {
        this.loading = true;
        paperlessBillingApi.enrollInPaperlessBilling.cancelPaperlessEnrollment()
          .then(() => {
            this.$refs.paperlessEnroll.style.display = 'block';
            this.success = false;
            this.isEnrolled = false;
            this.enrollText = 'Not Enrolled';
            // set the checkmark
            const greenCheck = select('.icon--green');
            if (greenCheck) {
              greenCheck.classList.remove('icon--green');
            }
            dataLayerPush('DirecteBillUnenroll');
            window.location.reload(true);
          })
          .catch(() => {
            this.loading = false;
          });
      });
      Event.$on('UpdatePaperlessEnrollment', (response) => {
        if (response && response.paperlessEmail) {
          this.success = false;
          this.update = true;
          this.paperlessEmail = response.paperlessEmail;
          this.emailValidationUpdateFailedCount = response.emailValidationFailedCount;
          this.enrollInPaperless();
        }
      });
    },

    /**
     * Computed
     */
    computed: {
      /**
       * Disable the "Enroll in Paperless Billing" button without valid email and I agree checkbox
       * @return {boolean}
       */
      shouldBeDisabled() {
        if (this.paperlessEmail && regex.email.test(this.paperlessEmail) && this.paperlessAgree === true && this.emailValidationFailedThresholdLimitReached === false) {
          return false;
        }
        return true;
      },
      enrollText() {
        if (this.isEnrolled) {
          return 'Enrolled';
        }
        return 'Not Enrolled';
      },
      emailValidationFailedThresholdLimitReached() {
        return this.emailValidationFailedCount >= 3;
      },
    },

    /**
     * Methods
     */
    methods: {
      enrollInPaperless() {
        this.loading = true;
        this.emailValidationFailed = false;
        if (window.fabricator) {
          setTimeout(() => {
            if (this.$refs.paperlessEnroll) {
              this.$refs.paperlessEnroll.style.display = 'none';
            }
            this.loading = false;
            this.success = true;
            this.isEnrolled = true;
            this.enrollText = 'Enrolled';
            this.scrollUp();
            // set the checkmark
            const statusBubble = select('.icon-success');
            if (statusBubble) {
              statusBubble.classList.add('icon--green');
            }
            if (this.update) {
              this.postUpdate();
            }
          }, 500);
        } else {
          paperlessBillingApi.enrollInPaperlessBilling(this.paperlessEmail, this.update === true ? this.emailValidationUpdateFailedCount : this.emailValidationFailedCount)
            .then(() => {
              if (this.$refs.paperlessEnroll) {
                this.$refs.paperlessEnroll.style.display = 'none';
              }
              this.loading = false;
              this.success = true;
              this.isEnrolled = true;
              this.enrollText = 'Enrolled';
              this.ebillEmail = this.paperlessEmail.trim().toLowerCase();
              this.hasMultiplePaperlessEmails = false;
              if (this.isEnrolled && this.ebillEmail !== this.webAccountEmail) {
                this.hasMultiplePaperlessEmails = true;
              }
              this.scrollUp();
              // set the checkmark
              const statusBubble = select('.icon-success');
              if (statusBubble) {
                statusBubble.classList.add('icon--green');
              }
              if (this.update) {
                this.postUpdate();
              }
              dataLayerPush('DirecteBillEnroll');
              portalSharedApi.updateClaims()
                .catch(err => {
                  console.log(err);
                });
            })
            .catch(err => {
              this.loading = false;
              if (this.update) {
                this.postUpdate(err);
              } else if (err.response.status === 400 && err.response.data.error.code === 1060) {
                removeErrors();
                this.emailValidationFailed = true;
                this.emailValidationFailedCount = this.emailValidationFailedCount + 1;
                cookies.set(paperlessEmailValidationCountCookieName, this.emailValidationFailedCount);
              }
              console.log(err);
            });
        }
      },
      updateEbillType() {
        this.isCheckfree = false;
        this.showCheckFreeWarning = false;
      },
      shouldHideCheckFreeWarning() {
        this.showCheckFreeWarning = false;
        this.showEbill = true;
      },
      postUpdate(err) {
        Event.$emit('ReturnEditPaperlessEnrollment', { success: this.success, err });
      },
    },
  });
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/portal/paperless.js